/**
 * BrainWelcomeScreen — first-time entry screen (Milestone 37.4 · locked 4c).
 *
 * When a founder arrives at ``/business-brain`` (or ``/brand-brain``)
 * for the first time and no approved brain exists, this screen offers
 * two choices — never a trap:
 *
 *   • Continue Setup — begins the guided mission immediately
 *   • Explore Orkexra First — routes to Executive Home; the
 *     "Teach Orkexra Your Business" tile keeps the mission one tap away
 */
import { useNavigate } from "react-router-dom";

import { persistBrainDeferral } from "./useFirstLoginBrainRedirect";
import type { BrainMissionType } from "@/entities/brain-missions";
import { useCurrentWorkspace } from "@/entities/workspace";

interface BrainWelcomeScreenProps {
  type: BrainMissionType;
  onContinue: () => void;
}

export function BrainWelcomeScreen({ type, onContinue }: BrainWelcomeScreenProps) {
  const navigate = useNavigate();
  const ws = useCurrentWorkspace();
  const brainLabel = type === "business_brain" ? "Business Brain" : "Brand Brain";
  const teachSubject =
    type === "business_brain" ? "your business" : "your brand voice";
  return (
    <div
      className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-start justify-center gap-8 px-6 py-16 text-white"
      data-testid="brain-welcome-screen"
      data-mission-type={type}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-300/80">
        {brainLabel}
      </div>
      <h1 className="text-3xl font-semibold leading-tight tracking-tight sm:text-4xl md:text-5xl">
        Let&apos;s teach Akinex how {teachSubject} thinks.
      </h1>
      <p className="max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
        This isn&apos;t onboarding. It&apos;s the one conversation that makes
        everything Akinex does for you afterwards feel like it actually
        came from you. Expect around 10&nbsp;minutes. You can pause
        anytime and Orkexra picks up exactly where you left off.
      </p>
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button
          type="button"
          onClick={onContinue}
          className="rounded-full bg-gradient-to-r from-cyan-400 via-sky-500 to-violet-500 px-6 py-3 text-sm font-semibold text-slate-950 shadow-[0_10px_30px_-10px_rgba(0,240,255,0.5)] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition"
          data-testid="brain-welcome-continue"
        >
          Continue Setup →
        </button>
        <button
          type="button"
          onClick={() => {
            if (ws?.id) persistBrainDeferral(ws.id);
            navigate("/home");
          }}
          className="rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white/80 hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 transition"
          data-testid="brain-welcome-explore"
        >
          Explore Orkexra First
        </button>
      </div>
      <div className="pt-4 text-xs text-white/50">
        If you choose Explore, we&apos;ll keep this waiting for you on your Executive Home
        so it&apos;s always one tap away.
      </div>
    </div>
  );
}
